import { __ } from '@wordpress/i18n';
import { 
    BaseControl,
    Button,
    TextControl,
    Dropdown,
    MenuGroup,
    MenuItem,
    SelectControl
} from '@wordpress/components';
import { useState, useRef } from '@wordpress/element';
import FlatColorPicker from '../FlatColorPicker';
import { Icons } from '../../utils/icons';  
// import './style.scss';

const DeviceIcon = ({ type }) => {
    switch (type) {
        case 'desktop':
            return (
                Icons.desktop
            );
        case 'tablet':
            return (
                Icons.tablet
            );
        case 'mobile':
            return (
                Icons.mobile
            );
        default:
            return null;
    }
};

const Border = ({ value = {}, onChange, label = __('Kenarlık', 'kan') }) => {
    const [activeDevice, setActiveDevice] = useState('desktop');
    const [isLinked, setIsLinked] = useState(true);
    const dropdownRef = useRef();

    const devices = [
        { name: 'desktop', label: __('Masaüstü', 'kan') },
        { name: 'tablet', label: __('Tablet', 'kan') },
        { name: 'mobile', label: __('Mobil', 'kan') }
    ];

    const edges = [
        { name: 'top', label: __('Üst', 'kan') },
        { name: 'right', label: __('Sağ', 'kan') },
        { name: 'bottom', label: __('Alt', 'kan') },
        { name: 'left', label: __('Sol', 'kan') }
    ];

    const borderStyles = [
        { value: 'none', label: __('Yok', 'kan') },
        { value: 'solid', label: __('Düz', 'kan') },
        { value: 'dashed', label: __('Kesikli', 'kan') },
        { value: 'dotted', label: __('Noktalı', 'kan') },
        { value: 'double', label: __('Çift', 'kan') },
        { value: 'groove', label: __('Oluk', 'kan') },
        { value: 'ridge', label: __('Sırt', 'kan') },
        { value: 'inset', label: __('İçe', 'kan') },
        { value: 'outset', label: __('Dışa', 'kan') }
    ];

    const updateValue = (property, newValue) => {
        if (isLinked) {
            // Create a new object with all properties updated
            const updatedValue = {
                ...value,
                [activeDevice]: {
                    ...(value[activeDevice] || {}),
                    [property]: newValue
                }
            };
            
            // If it's a nested property like width or radius, update all edges
            if (property === 'width' || property === 'radius') {
                const numValue = parseInt(newValue) || 0;
                
                if (property === 'width') {
                    updatedValue[activeDevice].width = {
                        top: numValue,
                        right: numValue,
                        bottom: numValue,
                        left: numValue
                    };
                } else if (property === 'radius') {
                    updatedValue[activeDevice].radius = {
                        topLeft: numValue,
                        topRight: numValue,
                        bottomRight: numValue,
                        bottomLeft: numValue
                    };
                }
            }
            
            onChange(updatedValue);
        } else {
            onChange({
                ...value,
                [activeDevice]: {
                    ...(value[activeDevice] || {}),
                    [property]: newValue
                }
            });
        }
    };

    const updateEdgeValue = (edge, property, newValue) => {
        const numValue = parseInt(newValue) || 0;
        
        if (isLinked) {
            // If linked, update all edges with the same value
            if (property === 'width') {
                updateValue('width', numValue);
            } else if (property === 'radius') {
                updateValue('radius', numValue);
            }
        } else {
            // If not linked, only update the specific edge
            onChange({
                ...value,
                [activeDevice]: {
                    ...(value[activeDevice] || {}),
                    [property]: {
                        ...(value[activeDevice]?.[property] || {}),
                        [edge]: numValue
                    }
                }
            });
        }
    };

    const getCurrentValue = (property, edge = null) => {
        if (!value[activeDevice]) return '';
        
        if (edge) {
            return value[activeDevice][property]?.[edge] || '';
        }
        
        return value[activeDevice][property] || '';
    };

    const resetValue = () => {
        onChange({});
    };

    const handleDeviceChange = (device) => {
        setActiveDevice(device);
        if (dropdownRef.current) {
            dropdownRef.current.toggle();
        }
    };

    const currentColor = getCurrentValue('color') || '';

    return (
        <BaseControl className="kan-control-item kan-border-control">
            <div className="kan-control-header">
                <span className="kan-control-label">{label}</span>
                <div className="kan-control-actions">
                    <Dropdown
                        ref={dropdownRef}
                        className="kan-device-dropdown"
                        contentClassName="kan-device-dropdown-content"
                        position="bottom right"
                        renderToggle={({ isOpen, onToggle }) => (
                            <Button
                                onClick={onToggle}
                                aria-expanded={isOpen}
                                className="kan-device-button"
                            >
                                <DeviceIcon type={activeDevice} />
                            </Button>
                        )}
                        renderContent={() => (
                            <MenuGroup>
                                {devices.map(device => (
                                    <MenuItem
                                        key={device.name}
                                        onClick={() => handleDeviceChange(device.name)}
                                        isSelected={activeDevice === device.name}
                                    >
                                        <DeviceIcon type={device.name} />
                                        <span>{device.label}</span>
                                    </MenuItem>
                                ))}
                            </MenuGroup>
                        )}
                    />
                    <Button
                        isSmall
                        isSecondary
                        onClick={resetValue}
                        className="kan-reset-button"
                        icon="image-rotate"
                    />
                </div>
            </div>

            <div className="kan-control-inputs">
                <div className="kan-control-section">
                    <div className="kan-control-label">{__('Köşe Yuvarlaklığı', 'kan')}</div>
                    <div className="kan-control-box">
                        <div className="kan-control-field">
                            <TextControl
                                type="number"
                                className="kan-control-input kan-control-top-left"
                                value={getCurrentValue('radius', 'topLeft')}
                                onChange={(value) => updateEdgeValue('topLeft', 'radius', value)}
                            />
                            <span className="kan-control-label">{__('Sol Üst', 'kan')}</span>
                        </div>
                        <div className="kan-control-field">
                            <TextControl
                                type="number"
                                className="kan-control-input kan-control-top-right"
                                value={getCurrentValue('radius', 'topRight')}
                                onChange={(value) => updateEdgeValue('topRight', 'radius', value)}
                            />
                            <span className="kan-control-label">{__('Sağ Üst', 'kan')}</span>
                        </div>
                        <div className="kan-control-field">
                            <TextControl
                                type="number"
                                className="kan-control-input kan-control-bottom-right"
                                value={getCurrentValue('radius', 'bottomRight')}
                                onChange={(value) => updateEdgeValue('bottomRight', 'radius', value)}
                            />
                            <span className="kan-control-label">{__('Sağ Alt', 'kan')}</span>
                        </div>
                        <div className="kan-control-field">
                            <TextControl
                                type="number"
                                className="kan-control-input kan-control-bottom-left"
                                value={getCurrentValue('radius', 'bottomLeft')}
                                onChange={(value) => updateEdgeValue('bottomLeft', 'radius', value)}
                            />
                            <span className="kan-control-label">{__('Sol Alt', 'kan')}</span>
                        </div>
                        <Button
                            className="kan-control-link"
                            isPrimary={isLinked}
                            onClick={() => setIsLinked(!isLinked)}
                            icon={isLinked ? 'admin-links' : 'editor-unlink'}
                        />
                    </div>
                </div>
                <div className="kan-control-section">
                    <div className="kan-control-label">{__('Kenarlık Stili', 'kan')}</div>
                    <SelectControl
                        value={getCurrentValue('style')}
                        options={borderStyles}
                        onChange={(value) => updateValue('style', value)}
                    />
                </div>

                <div className="kan-control-section">
                    <div className="kan-control-label">{__('Kenarlık Kalınlığı', 'kan')}</div>
                    <div className="kan-control-box">
                        {edges.map(edge => (
                            <div key={edge.name} className="kan-control-field">
                                <TextControl
                                    type="number"
                                    className={`kan-control-input kan-control-${edge.name}`}
                                    value={getCurrentValue('width', edge.name)}
                                    onChange={(value) => updateEdgeValue(edge.name, 'width', value)}
                                />
                                <span className="kan-control-label">{edge.label}</span>
                            </div>
                        ))}
                        <Button
                            className="kan-control-link"
                            isPrimary={isLinked}
                            onClick={() => setIsLinked(!isLinked)}
                            icon={isLinked ? 'admin-links' : 'editor-unlink'}
                        />
                    </div>
                </div>

                <div className="kan-control-section">
                    <div className="kan-control-label">{__('Kenarlık Rengi', 'kan')}</div>
                      <FlatColorPicker
                          value={currentColor}
                          onChange={(value) => updateValue('color', value)}
                      />
                </div>

                
            </div>
        </BaseControl>
    );
};

export default Border; 